import { LegacyRef, useRef } from "react";

import { Table, TableProps } from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import styles from "./DragSortingTable.module.scss";

const type = "DraggableBodyRow";
interface RowProps {
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
}

export type DraggableRowProps = RowProps & React.HTMLAttributes<HTMLElement>;

/**
 * Table row wrapper that wires react-dnd hooks into Ant Design rows so they can be reordered.
 * Consumers pass the memoized `moveRow` callback, and the drag/drop handlers take care of invoking
 * it with the original and hovered indices.
 */
export function DraggableBodyRow({ index, className, style, moveRow, ...restProps }: DraggableRowProps): JSX.Element {
    const ref = useRef();
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: type,
        collect: (monitor) => {
            const { index: dragIndex } = monitor.getItem() || {};
            if (dragIndex === index) {
                return {};
            }
            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < index ? " drop-over-downward" : " drop-over-upward",
            };
        },
        drop: (item: any) => {
            moveRow(item.index, index);
        },
    });
    const [, drag] = useDrag({
        type: "DraggableBodyRow",
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    drop(drag(ref));

    return (
        <tr
            ref={ref as unknown as LegacyRef<HTMLTableRowElement>}
            className={`${styles.draggableCursor} ${className} ${isOver ? dropClassName : ""}`}
            style={{ ...style }}
            {...restProps}
        />
    );
}

/**
 * Thin Table wrapper that scopes the DnD context to each grid instance. We keep this in its own
 * component to avoid having to mount a global provider or worry about nested grids sharing drag
 * state.
 */
export function DragSortingTable(props: TableProps<any>): JSX.Element {
    return (
        <DndProvider backend={HTML5Backend}>
            <Table {...props} />
        </DndProvider>
    );
}
